// The installable app: its head tags, and the browser side of `public/sw.js`, which keeps the
// app shell and a few capture answers so the installed app opens on site with no signal.

import { logger } from "@/lib/logger";

/** The message `public/sw.js` answers by dropping every cached API answer. */
const CLEAR_API_CACHE = "clear-api-cache";

/**
 * Bun's HTML bundler resolves every `link[href]` in index.html at build time, so absolute
 * paths to files in public/ cannot be written there; they are added here instead. A tag
 * index.html already carries is left alone: it sets the theme color (from tokens.css) and
 * Apple's touch icon, which is the Madison mark from design-system/assets.
 */
export function addInstallTags() {
  addHeadTag("link", "rel", "manifest", { href: "/manifest.webmanifest" });
  addHeadTag("meta", "name", "apple-mobile-web-app-capable", { content: "yes" });
  // Chrome's name for the same thing; it warns that Apple's is deprecated.
  addHeadTag("meta", "name", "mobile-web-app-capable", { content: "yes" });
  addHeadTag("link", "rel", "apple-touch-icon", { href: "/icons/icon-192.png" });
}

function addHeadTag(
  tag: "link" | "meta",
  key: "rel" | "name",
  value: string,
  attributes: Record<string, string>,
) {
  if (document.head.querySelector(`${tag}[${key}="${value}"]`)) return;
  const element = document.createElement(tag);
  element.setAttribute(key, value);
  for (const [name, attr] of Object.entries(attributes)) element.setAttribute(name, attr);
  document.head.appendChild(element);
}

export function registerServiceWorker() {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
  navigator.serviceWorker.register("/sw.js", { scope: "/" }).catch((error: unknown) => {
    logger.warn("service_worker_register_failed", { error: String(error) });
  });
}

/**
 * Sign-in and sign-out call this, so the next person on the phone never sees the cached
 * project list, checklist or session of the one before.
 */
export function clearServiceWorkerApiCache() {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
  navigator.serviceWorker.controller?.postMessage({ type: CLEAR_API_CACHE });
  // A worker that is installed but not yet controlling this page still holds the cache.
  navigator.serviceWorker.ready
    .then((registration) => registration.active?.postMessage({ type: CLEAR_API_CACHE }))
    .catch(() => undefined);
}
